/**
 * Brand Page - Static JSON Version
 * Loads pre-generated JSON catalog data for instant page loads
 * 
 * JSON files are generated by: npm run build:catalog
 * Version: 3.0 - ONE accordion at a time (mutual exclusion)
 */

(function() {
  'use strict';

  // ============================================
  // CONFIGURATION
  // ============================================

  const CONFIG = {
    // Path to catalog data files (relative to assets)
    catalogDataPath: '/cdn/shop/files/',  // Shopify CDN path
    assetsPath: window.Shopify?.routes?.root || '/',
    cacheKey: 'brand_catalog_cache_v2',
    cacheDuration: 1000 * 60 * 5, // 5 minutes
  };

  // ============================================
  // FETCH BRAND DATA FROM STATIC JSON
  // ============================================

  async function fetchBrandData(brandName) {
    // Generate the filename from brand name
    const filename = `catalog-${brandName.toLowerCase().replace(/\s+/g, '-')}.json`;
    
    // Get the asset URL - this will be set by Liquid in the HTML
    const assetBaseUrl = window.catalogAssetUrl || '';
    
    // Add cache-busting parameter
    const cacheBuster = `?v=${Date.now()}`;
    
    // Build the URL - if we have a Liquid-provided base URL, use it
    // Otherwise try common Shopify asset paths
    const paths = assetBaseUrl ? 
      [assetBaseUrl.replace('catalog-index.json', filename) + cacheBuster] :
      [
        `/assets/${filename}${cacheBuster}`,
        `${window.Shopify?.routes?.root || '/'}assets/${filename}${cacheBuster}`,
      ];
    
    console.log(`📦 Loading catalog data for ${brandName}...`);
    
    // Try each path
    for (const url of paths) {
      try {
        console.log(`   Trying: ${url}`);
        const response = await fetch(url, { cache: 'no-cache' });
        if (response.ok) {
          const data = await response.json();
          console.log(`✅ Loaded ${brandName} data: ${data.families?.length || 0} families`);
          return data;
        }
      } catch (e) {
        // Try next path
      }
    }
    
    // If static JSON not found
    console.warn(`⚠️ Static JSON not found for ${brandName}`);
    return null;
  }

  // ============================================
  // PROCESS JSON DATA INTO UI FORMAT
  // ============================================

  function processJsonData(brandData) {
    const families = [];
    const models = [];
    
    if (!brandData || !brandData.families) {
      return { families, models };
    }
    
    brandData.families.forEach(family => {
      // Add family
      families.push({
        title: family.title || family.name,
        handle: family.handle,
        url: `/collections/${family.handle}`,
        modelCount: family.modelCount || family.models?.length || 0
      });
      
      // Add all models for this family
      if (family.models && Array.isArray(family.models)) {
        family.models.forEach(model => {
          models.push({
            title: model.name,
            handle: model.handle,
            familySeries: family.name,
            url: `/collections/${model.handle}`,
            productsCount: model.productsCount || 0
          });
        });
      }
    });
    
    console.log(`📊 Processed: ${families.length} families, ${models.length} models`);
    return { families, models };
  }

  function buildFinderData(families, models) {
    const finderData = {};
    
    families.forEach(family => {
      // Match models to families by familySeries
      const familyModels = models.filter(model => {
        if (!model.familySeries) return false;
        
        // Normalize both for comparison
        const modelFamily = (model.familySeries + '').toLowerCase().replace(/\s+/g, '-');
        const familyName = (family.title + '').toLowerCase().replace(/\s+/g, '-');
        
        return modelFamily === familyName || model.familySeries === family.title;
      });
      
      if (familyModels.length > 0) {
        finderData[family.title] = familyModels.map(m => ({
          title: m.title,
          handle: m.handle,
          url: m.url
        }));
      }
    });
    
    return finderData;
  }

  function cleanTitle(title, brandName) {
    const brands = ['brother', 'hp', 'canon', 'epson', 'lexmark', 'samsung', 'xerox', 'dell', 'ricoh', 'kyocera'];
    let t = (title || '').trim();
    
    for (const brand of brands) {
      const regex = new RegExp('^' + brand + '\\s+', 'i');
      if (regex.test(t)) {
        t = t.replace(regex, '');
        break;
      }
    }
    
    return t || title;
  }

  // ============================================
  // UI RENDERING
  // ============================================

  function renderFinderTool(finderData, sectionId) {
    const loadingIndicator = document.getElementById(`loading-finder-${sectionId}`);
    
    if (loadingIndicator) {
      loadingIndicator.style.display = 'none';
    }
    
    // Dropdowns removed - search-only interface
    console.log('✅ Finder ready - search enabled');
  }

  function renderTypeAheadSearch(models, currentBrand, sectionId) {
    const searchInput = document.getElementById(`printer-search-${sectionId}`);
    const searchResults = document.getElementById(`search-results-${sectionId}`);
    const loadingIndicator = document.getElementById(`loading-finder-${sectionId}`);
    
    if (!searchInput || !searchResults) return;
    
    // Hide loading indicator
    if (loadingIndicator) {
      loadingIndicator.style.display = 'none';
    }
    
    let debounceTimer;
    let selectedIndex = -1;
    
    // Clean model titles for display
    const cleanedModels = models.map(m => ({
      ...m,
      cleanTitle: cleanTitle(m.title, currentBrand)
    }));
    
    // Handle search input
    function handleSearch(query) {
      clearTimeout(debounceTimer);
      
      debounceTimer = setTimeout(() => {
        if (query.length < 2) {
          searchResults.style.display = 'none';
          return;
        }
        
        performSearch(query);
      }, 300);
    }
    
    // Perform search
    function performSearch(query) {
      const normalizedQuery = query.toLowerCase().trim();
      
      // Filter and sort results
      const results = cleanedModels
        .filter(model => {
          const title = model.title.toLowerCase();
          const cleanTitle = model.cleanTitle.toLowerCase();
          const handle = model.handle.toLowerCase();
          return title.includes(normalizedQuery) || 
                 cleanTitle.includes(normalizedQuery) || 
                 handle.includes(normalizedQuery);
        })
        .sort((a, b) => {
          // Exact matches first
          const aClean = a.cleanTitle.toLowerCase();
          const bClean = b.cleanTitle.toLowerCase();
          
          if (aClean.startsWith(normalizedQuery)) return -1;
          if (bClean.startsWith(normalizedQuery)) return 1;
          return aClean.localeCompare(bClean);
        })
        .slice(0, 10); // Limit to 10 results
      
      displayResults(results, query);
    }
    
    // Display search results
    function displayResults(results, query) {
      if (results.length === 0) {
        searchResults.innerHTML = `
          <div class="no-results">
            No models found. Try a different search or 
            <a href="/pages/contact-us">Contact us</a>
          </div>
        `;
        searchResults.style.display = 'block';
        return;
      }
      
      searchResults.innerHTML = results.map(model => `
        <a href="${model.url}" class="search-result-item">
          ${highlightMatch(model.cleanTitle, query)}
        </a>
      `).join('');
      
      searchResults.style.display = 'block';
      selectedIndex = -1;
    }
    
    // Highlight matching text
    function highlightMatch(text, query) {
      const regex = new RegExp(`(${escapeRegex(query)})`, 'gi');
      return text.replace(regex, '<strong>$1</strong>');
    }
    
    // Escape regex special characters
    function escapeRegex(str) {
      return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    }
    
    // Update selection (keyboard navigation)
    function updateSelection() {
      const items = searchResults.querySelectorAll('.search-result-item');
      items.forEach((item, index) => {
        if (index === selectedIndex) {
          item.classList.add('active');
          item.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        } else {
          item.classList.remove('active');
        }
      });
    }
    
    // Event: Input
    searchInput.addEventListener('input', (e) => {
      handleSearch(e.target.value);
    });
    
    // Event: Focus
    searchInput.addEventListener('focus', (e) => {
      if (e.target.value.length >= 2) {
        performSearch(e.target.value);
      }
    });
    
    // Event: Keyboard navigation
    searchInput.addEventListener('keydown', (e) => {
      const items = searchResults.querySelectorAll('.search-result-item');
      
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        selectedIndex = Math.min(selectedIndex + 1, items.length - 1);
        updateSelection();
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        selectedIndex = Math.max(selectedIndex - 1, -1);
        updateSelection();
      } else if (e.key === 'Enter' && selectedIndex >= 0) {
        e.preventDefault();
        items[selectedIndex].click();
      } else if (e.key === 'Escape') {
        searchResults.style.display = 'none';
        selectedIndex = -1;
      }
    });
    
    // Event: Click outside to close
    document.addEventListener('click', (e) => {
      if (!e.target.closest('.search-container')) {
        searchResults.style.display = 'none';
        selectedIndex = -1;
      }
    });
  }

  function renderFinderTool(finderData, sectionId) {
    const seriesSelect = document.getElementById(`printer-series-${sectionId}`);
    const modelSelect = document.getElementById(`printer-model-${sectionId}`);
    const loadingIndicator = document.getElementById(`loading-finder-${sectionId}`);
    
    if (!seriesSelect || !modelSelect) return;
    
    if (loadingIndicator) {
      loadingIndicator.style.display = 'none';
    }
    
    const sortedSeries = Object.keys(finderData).sort();
    seriesSelect.innerHTML = '<option value="">Select Printer Series...</option>';
    
    sortedSeries.forEach(series => {
      const option = document.createElement('option');
      option.value = series;
      option.textContent = series;
      seriesSelect.appendChild(option);
    });
    
    seriesSelect.disabled = false;
    
    seriesSelect.addEventListener('change', function() {
      const selectedSeries = this.value;
      
      if (!selectedSeries) {
        modelSelect.innerHTML = '<option value="">Select Printer Model...</option>';
        modelSelect.disabled = true;
        return;
      }
      
      const models = finderData[selectedSeries] || [];
      modelSelect.innerHTML = '<option value="">Select Printer Model...</option>';
      
      const sortedModels = models.sort((a, b) => a.title.localeCompare(b.title));
      
      sortedModels.forEach(model => {
        const option = document.createElement('option');
        option.value = model.url;
        option.textContent = model.title;
        modelSelect.appendChild(option);
      });
      
      modelSelect.disabled = false;
    });
    
    modelSelect.addEventListener('change', function() {
      const selectedUrl = this.value;
      if (selectedUrl) {
        window.location.href = selectedUrl;
      }
    });
  }

  function renderFamilyAccordion(families, models, currentBrand, sectionId) {
    const container = document.getElementById(`family-accordion-${sectionId}`);
    
    if (!container) return;
    
    if (families.length === 0) {
      container.innerHTML = `
        <div class="error-message">
          <h3>No printer families found</h3>
          <p>We couldn't find any printer families for ${currentBrand}.</p>
          <p>Please <a href="/pages/contact-us">contact us</a> for assistance.</p>
        </div>
      `;
      return;
    }
    
    container.innerHTML = '';
    
    // Create two independent columns
    const leftColumn = document.createElement('div');
    leftColumn.className = 'family-accordion-column';
    const rightColumn = document.createElement('div');
    rightColumn.className = 'family-accordion-column';
    container.appendChild(leftColumn);
    container.appendChild(rightColumn);
    
    const sortedFamilies = families.sort((a, b) => a.title.localeCompare(b.title));
    
    sortedFamilies.forEach((family, index) => {
      // Get models for this family
      const familyModels = models.filter(model => {
        if (!model.familySeries) return false;
        
        // Normalize both for comparison
        const modelFamily = (model.familySeries + '').toLowerCase().replace(/\s+/g, '-');
        const familyName = (family.title + '').toLowerCase().replace(/\s+/g, '-');
        
        return modelFamily === familyName || model.familySeries === family.title;
      });
      
      const sortedModels = familyModels.sort((a, b) => a.title.localeCompare(b.title));
      const modelCount = sortedModels.length;
      
      // Create family section
      const section = document.createElement('div');
      section.className = 'family-accordion-item';
      // Don't auto-open first item - let users choose
      
      // Family header
      const header = document.createElement('button');
      header.type = 'button';
      header.className = 'family-accordion-header';
      header.setAttribute('aria-expanded', 'false');
      
      header.innerHTML = `
        <div class="header-left">
          <span class="accordion-icon">▶</span>
          <h3 class="family-title">
            <a href="${family.url}" onclick="event.stopPropagation();">
              ${cleanTitle(family.title, currentBrand)}
            </a>
          </h3>
          <span class="model-count">(${modelCount} model${modelCount !== 1 ? 's' : ''})</span>
        </div>
      `;
      
      // Family content (CSS handles show/hide via .active class on parent)
      const content = document.createElement('div');
      content.className = 'family-accordion-content';
      
      if (sortedModels.length > 0) {
        content.innerHTML = renderAccordionContent(family, sortedModels, currentBrand);
      } else {
        content.innerHTML = `
          <div class="no-models-message">
            <p>No models found for this family.</p>
            <p><a href="/pages/contact-us">Contact us</a> for assistance.</p>
          </div>
        `;
      }
      
      // Toggle functionality - each accordion independent
      // CSS handles display via .active class + align-items: start prevents row stretching
      header.addEventListener('click', function(e) {
        if (e.target.tagName === 'A') {
          return;
        }
        
        e.preventDefault();
        
        // Simply toggle this accordion
        if (section.classList.contains('active')) {
          section.classList.remove('active');
          this.setAttribute('aria-expanded', 'false');
        } else {
          section.classList.add('active');
          this.setAttribute('aria-expanded', 'true');
        }
      });
      
      section.appendChild(header);
      section.appendChild(content);
      // Alternate between left and right columns
      if (index % 2 === 0) {
        leftColumn.appendChild(section);
      } else {
        rightColumn.appendChild(section);
      }
    });
    
    // Initialize progressive loading functionality
    initProgressiveLoading();
  }
  
  // Render accordion content with progressive loading
  function renderAccordionContent(family, models, currentBrand) {
    const INITIAL_SHOW = 20;
    const totalModels = models.length;
    const showSearch = totalModels > 30;
    
    // Generate model links
    const modelLinks = models.map((model, index) => {
      const hiddenClass = index >= INITIAL_SHOW ? 'hidden' : '';
      const cleanedTitle = cleanTitle(model.title, currentBrand);
      
      return `
        <a href="${model.url}" 
           class="model-link ${hiddenClass}" 
           data-index="${index}">
          ${cleanedTitle}
        </a>
      `;
    }).join('');
    
    const remaining = Math.max(0, totalModels - INITIAL_SHOW);
    const showLoadMore = remaining > 0;
    
    return `
      <div class="model-help-message">
        Can't find your model? <a href="/pages/contact-us">Contact us</a> 
        and we'll help you find the right cartridge.
      </div>
      
      ${showSearch ? `
        <div class="accordion-search">
          <div class="search-icon">🔍</div>
          <input 
            type="text" 
            class="model-search-input"
            placeholder="Search ${cleanTitle(family.title, currentBrand)} models..."
            autocomplete="off"
          >
        </div>
      ` : ''}
      
      <div class="model-links-grid" data-total-models="${totalModels}">
        ${modelLinks}
      </div>
      
      ${showLoadMore ? `
        <div class="load-more-section">
          <div class="models-count">Showing ${INITIAL_SHOW} of ${totalModels} models</div>
          <div class="load-more-buttons">
            <button type="button" class="load-more-btn" data-batch-size="20">
              Load 20 More
            </button>
            <button type="button" class="show-all-btn">
              Show All ${totalModels} Models
            </button>
          </div>
        </div>
      ` : ''}
    `;
  }
  
  // Initialize progressive loading functionality
  function initProgressiveLoading() {
    // Search within accordion
    document.querySelectorAll('.model-search-input').forEach(input => {
      input.addEventListener('input', function() {
        const query = this.value.toLowerCase().trim();
        const content = this.closest('.family-accordion-content');
        const grid = content.querySelector('.model-links-grid');
        const links = grid.querySelectorAll('.model-link');
        const section = content.querySelector('.load-more-section');
        
        if (query === '') {
          // Reset to original state
          links.forEach((link, index) => {
            link.classList.remove('search-hidden');
            if (index >= 20) {
              link.classList.add('hidden');
            }
          });
          if (section) {
            section.classList.remove('search-active');
            updateCount(content);
          }
        } else {
          // Filter by search
          let matchCount = 0;
          links.forEach(link => {
            link.classList.remove('hidden'); // Remove load-more hiding
            const text = link.textContent.toLowerCase();
            if (text.includes(query)) {
              link.classList.remove('search-hidden');
              matchCount++;
            } else {
              link.classList.add('search-hidden');
            }
          });
          
          if (section) {
            section.classList.add('search-active');
            const countEl = section.querySelector('.models-count');
            countEl.textContent = `Showing ${matchCount} matching model${matchCount !== 1 ? 's' : ''}`;
          }
        }
      });
    });
    
    // Load more functionality
    document.querySelectorAll('.load-more-btn').forEach(btn => {
      btn.addEventListener('click', function() {
        const content = this.closest('.family-accordion-content');
        const grid = content.querySelector('.model-links-grid');
        const batchSize = parseInt(this.dataset.batchSize) || 20;
        
        const allLinks = grid.querySelectorAll('.model-link');
        const hiddenLinks = Array.from(allLinks).filter(link => 
          link.classList.contains('hidden')
        );
        
        // Show next batch
        const toShow = Math.min(batchSize, hiddenLinks.length);
        for (let i = 0; i < toShow; i++) {
          hiddenLinks[i].classList.remove('hidden');
        }
        
        updateCount(content);
      });
    });
    
    // Show all functionality
    document.querySelectorAll('.show-all-btn').forEach(btn => {
      btn.addEventListener('click', function() {
        const content = this.closest('.family-accordion-content');
        const grid = content.querySelector('.model-links-grid');
        const links = grid.querySelectorAll('.model-link');
        
        // Show all models
        links.forEach(link => link.classList.remove('hidden'));
        
        const section = content.querySelector('.load-more-section');
        if (section) {
          section.classList.add('all-shown');
          updateCount(content);
        }
      });
    });
  }
  
  // Update count display
  function updateCount(content) {
    const grid = content.querySelector('.model-links-grid');
    const section = content.querySelector('.load-more-section');
    if (!section) return;
    
    const countEl = section.querySelector('.models-count');
    const loadMoreBtn = section.querySelector('.load-more-btn');
    
    const allLinks = grid.querySelectorAll('.model-link');
    const visibleLinks = Array.from(allLinks).filter(link => 
      !link.classList.contains('hidden') && !link.classList.contains('search-hidden')
    );
    
    const totalModels = allLinks.length;
    const shown = visibleLinks.length;
    const remaining = totalModels - shown;
    
    if (remaining > 0) {
      countEl.textContent = `Showing ${shown} of ${totalModels} models`;
      if (loadMoreBtn) {
        const toLoad = Math.min(20, remaining);
        loadMoreBtn.textContent = `Load ${toLoad} More`;
      }
    } else {
      countEl.textContent = `Showing all ${totalModels} models`;
      section.classList.add('all-shown');
    }
  }

  function showError(sectionId, message) {
    const finderLoading = document.getElementById(`loading-finder-${sectionId}`);
    const container = document.getElementById(`family-accordion-${sectionId}`);
    
    if (finderLoading) {
      finderLoading.style.display = 'none';
    }
    
    if (container) {
      container.innerHTML = `
        <div class="error-message">
          <h3>Unable to Load Data</h3>
          <p>We're having trouble loading the printer data. Please try refreshing the page.</p>
          <p>If the problem persists, <a href="/pages/contact-us">contact us</a> for assistance.</p>
        </div>
      `;
    }
  }

  // ============================================
  // INITIALIZATION
  // ============================================

  async function initBrandPage() {
    console.log('🚀 Brand Page JS v4.0 - CSS-only toggle, no inline styles');
    
    const brandPageElements = document.querySelectorAll('.brand-page-api');
    
    if (brandPageElements.length === 0) {
      console.log('❌ No brand page elements found');
      return;
    }
    
    for (const element of brandPageElements) {
      const currentBrand = element.dataset.brand;
      const sectionId = element.dataset.sectionId;
      
      console.log('🏷️  Current Brand:', currentBrand);
      console.log('🆔 Section ID:', sectionId);
      
      if (!currentBrand) {
        showError(sectionId, 'Brand name not found');
        return;
      }
      
      try {
        // Check cache first
        const brandCacheKey = `${CONFIG.cacheKey}_${currentBrand.toLowerCase()}`;
        let brandData = null;
        
        const cachedData = sessionStorage.getItem(brandCacheKey);
        if (cachedData) {
          try {
            const { data, timestamp } = JSON.parse(cachedData);
            if (Date.now() - timestamp < CONFIG.cacheDuration) {
              brandData = data;
              console.log('📦 Using cached data for', currentBrand);
            }
          } catch (e) {}
        }
        
        // Fetch from static JSON if not cached
        if (!brandData) {
          brandData = await fetchBrandData(currentBrand);
          
          if (brandData) {
            // Cache the data
            try {
              sessionStorage.setItem(brandCacheKey, JSON.stringify({
                data: brandData,
                timestamp: Date.now()
              }));
            } catch (e) {}
          }
        }
        
        if (!brandData || !brandData.families || brandData.families.length === 0) {
          throw new Error('No catalog data found for this brand');
        }
        
        // Process the JSON data into UI format
        const { families, models } = processJsonData(brandData);
        
        console.log('✅ Final Results:');
        console.log(`   Families: ${families.length}`);
        console.log(`   Models: ${models.length}`);
        
        const finderData = buildFinderData(families, models);
        
        // Render dropdown finder (original)
        renderFinderTool(finderData, sectionId);
        
        // Render type-ahead search (new additional option)
        renderTypeAheadSearch(models, currentBrand, sectionId);
        
        // Render family accordions
        renderFamilyAccordion(families, models, currentBrand, sectionId);
        
      } catch (error) {
        console.error('❌ Brand page initialization failed:', error);
        showError(sectionId, error.message);
      }
    }
  }

  // ============================================
  // PAGE LOAD
  // ============================================

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initBrandPage);
  } else {
    initBrandPage();
  }

  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:section:load', initBrandPage);
  }

})();
